import React, { useEffect, useState } from 'react';
import { getChatAudience, AudienceMember } from './api';

/** Arbre d'audience : 🌍 Tout le monde ▾ → 👥 Équipes ▾ → membres (cases à cocher).
 *  Réutilisé dans la création de canal et la gestion des membres. */
export default function AudienceTree({
  everyone, teams, memberIds, onEveryone, onTeam, onMember, canGrantBroad, renderExtra,
}: {
  everyone: boolean;
  teams: Set<string>;
  memberIds: Set<number>;
  onEveryone?: (v: boolean) => void;
  onTeam?: (g: string, v: boolean) => void;
  onMember: (uid: number, v: boolean) => void;
  canGrantBroad?: boolean;
  renderExtra?: (m: AudienceMember) => React.ReactNode;
}) {
  const [data, setData] = useState<{ all: AudienceMember[]; teams: { groupe: string; members: AudienceMember[] }[] } | null>(null);
  const [rootOpen, setRootOpen] = useState(true);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');

  useEffect(() => { getChatAudience().then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div style={{ padding: 12, fontSize: 12, color: '#94a3b8' }}>Chargement…</div>;

  const noTeam = data.all.filter(m => !m.groupe);
  const buckets = [...data.teams, ...(noTeam.length ? [{ groupe: 'Sans équipe', members: noTeam }] : [])];
  const toggleOpen = (g: string) => setOpen(s => { const n = new Set(s); n.has(g) ? n.delete(g) : n.add(g); return n; });

  // Ligne membre réutilisée (arbre + résultats de recherche)
  const memberRow = (m: AudienceMember, indent: number, showTeam?: boolean) => {
    const covered = everyone || (!!m.groupe && teams.has(m.groupe));
    const checked = covered || memberIds.has(m.user_id);
    return (
      <Row key={m.user_id} indent={indent}>
        <span style={{ width: 15 }} />
        <input type="checkbox" checked={checked} disabled={covered}
          onChange={e => onMember(m.user_id, e.target.checked)} style={cb} />
        <MiniAvatar url={m.avatar_url} name={m.display_name} />
        <span style={{ fontSize: 12.5, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {m.display_name}{showTeam && m.groupe && <span style={{ fontSize: 10, color: '#94a3b8' }}> · {m.groupe}</span>}
        </span>
        {renderExtra && renderExtra(m)}
      </Row>
    );
  };

  const query = q.trim().toLowerCase();
  const results = query
    ? data.all.filter(m => (m.display_name || '').toLowerCase().includes(query) || (m.username || '').toLowerCase().includes(query))
    : [];

  return (
    <div>
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un membre…"
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: '8px 30px 8px 11px', fontSize: 13, outline: 'none' }} />
        {q ? <button onClick={() => setQ('')} style={{ position: 'absolute', right: 6, top: 6, background: 'none', border: 'none', color: '#94a3b8', fontSize: 16, cursor: 'pointer', padding: '2px 6px' }}>×</button>
           : <span style={{ position: 'absolute', right: 10, top: 8, color: '#cbd5e1', fontSize: 13 }}>🔍</span>}
      </div>

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        {query ? (
          results.length === 0
            ? <div style={{ padding: 16, fontSize: 12.5, color: '#94a3b8', textAlign: 'center' }}>Aucun membre trouvé.</div>
            : results.map(m => memberRow(m, 0, true))
        ) : (
          <>
            {/* Racine : Tout le monde */}
            <Row indent={0} bold>
              <Caret open={rootOpen} onClick={() => setRootOpen(o => !o)} />
              {canGrantBroad
                ? <input type="checkbox" checked={everyone} onChange={e => onEveryone?.(e.target.checked)} style={cb} />
                : <span style={{ width: 15 }} />}
              <span style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a' }}>🌍 Tout le monde</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>{data.all.length}</span>
            </Row>

            {rootOpen && buckets.map(bk => {
              const g = bk.groupe;
              const isTeam = g !== 'Sans équipe';
              const teamChecked = everyone || (isTeam && teams.has(g));
              const teamDisabled = everyone;
              const opened = open.has(g);
              return (
                <div key={g}>
                  <Row indent={1}>
                    <Caret open={opened} onClick={() => toggleOpen(g)} />
                    {canGrantBroad && isTeam
                      ? <input type="checkbox" checked={teamChecked} disabled={teamDisabled}
                          onChange={e => onTeam?.(g, e.target.checked)} style={cb} />
                      : <span style={{ width: 15 }} />}
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>{isTeam ? '👥 ' : '📭 '}{g}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>{bk.members.length}</span>
                  </Row>
                  {opened && bk.members.map(m => memberRow(m, 2))}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ children, indent, bold }: { children: React.ReactNode; indent: number; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', paddingLeft: 10 + indent * 20, borderBottom: '1px solid #f1f5f9', background: bold ? '#faf5ff' : '#fff' }}>
      {children}
    </div>
  );
}

function Caret({ open, onClick }: { open: boolean; onClick: () => void }) {
  return <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 11, width: 14, padding: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</button>;
}

function MiniAvatar({ url, name }: { url?: string | null; name?: string }) {
  const [err, setErr] = useState(false);
  const proxied = url && /^https?:\/\//i.test(url) ? `/api/gift-image?url=${encodeURIComponent(url)}` : url;
  const initials = (name || '?').replace(/[^\p{L}\p{N} ]/gu, '').trim().slice(0, 2).toUpperCase() || '?';
  return (
    <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', fontWeight: 600, fontSize: 9, flexShrink: 0, overflow: 'hidden' }}>
      {proxied && !err ? <img src={proxied} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setErr(true)} /> : initials}
    </div>
  );
}

const cb: React.CSSProperties = { width: 16, height: 16, cursor: 'pointer', flexShrink: 0 };
